// Libs
import { TFunction } from 'i18next';
import * as yup from 'yup';
// Others
import { DEFAULT_NUMBER_ZERO, EMPTY_STRING, MAX_SIZE_FILE_UPLOAD } from '~/utils/constants/common';
import { IFormService, IServiceLabor, IServiceMaterial } from '~/utils/interface/service';

export const schema = (t: TFunction) => {
  return yup
    .object()
    .shape({
      serviceImage: yup
        .object()
        .shape({
          id: yup.string().trim().optional(),
          url: yup.string().trim().optional(),
          file: yup
            .mixed<File>()
            .optional()
            .test('fileSize', t('common_validate_file_size', { ns: 'common/base' }), (value) => {
              if (!value) return true;
              if (value && value?.size <= MAX_SIZE_FILE_UPLOAD) return true;
              return false;
            }),
        })
        .optional(),
      name: yup
        .string()
        .trim()
        .required(t('common_validate_field_required', { ns: 'common/base' })),
      unit: yup.string().trim().optional(),
      categoryId: yup
        .string()
        .trim()
        .required(t('common_validate_field_required', { ns: 'common/base' })),
      materials: yup.array().of(
        yup.object().shape(
          {
            materialId: yup
              .string()
              .trim()
              .when('quantity', {
                is: (val: string) => !!val,
                then: (s) => s.required(t('common_validate_field_required', { ns: 'common/base' })),
                otherwise: (s) => s.optional(),
              }),
            name: yup.string().trim().optional(),
            quantity: yup
              .string()
              .trim()
              .when('materialId', {
                is: (val: string) => !!val,
                then: (s) => s.required(t('common_validate_field_required', { ns: 'common/base' })),
                otherwise: (s) => s.optional(),
              }),
            unitCost: yup.string().trim().optional(),
            unitPrice: yup.string().trim().optional(),
            totalCost: yup.string().trim().optional(),
            totalPrice: yup.string().trim().optional(),
          },
          [['materialId', 'quantity']]
        )
      ),
      labors: yup.array().of(
        yup.object().shape(
          {
            description: yup.string().trim().optional(),
            hourlyCost: yup
              .string()
              .trim()
              .when('hourlyPrice', {
                is: (val: string) => !!val,
                then: (s) => s.required(t('common_validate_field_required', { ns: 'common/base' })),
                otherwise: (s) => s.optional(),
              }),
            hourlyPrice: yup
              .string()
              .trim()
              .when('hourlyCost', {
                is: (val: string) => !!val,
                then: (s) => s.required(t('common_validate_field_required', { ns: 'common/base' })),
                otherwise: (s) => s.optional(),
              }),
          },
          [['hourlyCost', 'hourlyPrice']]
        )
      ),
      description: yup.string().trim().optional(),
      taskCode: yup.string().trim().optional(),
      isTaxable: yup.boolean().optional(),
      totalCost: yup.number().optional(),
      totalPrice: yup.number().optional(),
    })
    .required();
};

export const defaultValuesForm: IFormService = {
  serviceImage: {},
  name: EMPTY_STRING,
  description: EMPTY_STRING,
  unit: EMPTY_STRING,
  taskCode: EMPTY_STRING,
  isTaxable: false,
  categoryId: EMPTY_STRING,
  materials: [
    {
      materialId: EMPTY_STRING,
      name: EMPTY_STRING,
      quantity: EMPTY_STRING,
      unitCost: EMPTY_STRING,
      unitPrice: EMPTY_STRING,
      totalCost: EMPTY_STRING,
      totalPrice: EMPTY_STRING,
    },
  ],
  labors: [
    {
      description: EMPTY_STRING,
      hourlyCost: EMPTY_STRING,
      hourlyPrice: EMPTY_STRING,
    },
  ],
  totalCost: DEFAULT_NUMBER_ZERO,
  totalPrice: DEFAULT_NUMBER_ZERO,
};

export const defaultValuesFormMaterial: IServiceMaterial = {
  materialId: EMPTY_STRING,
  quantity: EMPTY_STRING,
  unitCost: EMPTY_STRING,
  unitPrice: EMPTY_STRING,
  totalCost: EMPTY_STRING,
  totalPrice: EMPTY_STRING,
};

export const defaultValuesFormLabor: IServiceLabor = {
  description: EMPTY_STRING,
  hourlyCost: EMPTY_STRING,
  hourlyPrice: EMPTY_STRING,
};

export const convertDataUpdate = <T extends { id?: string }>(
  current: T[] = [],
  initial: T[] = []
): {
  updated: T[];
  created: T[];
  deleted: string[];
} => {
  const updated: T[] = [];
  const created: T[] = [];

  current.forEach((item) => {
    if (item.id) {
      updated.push(item);
    } else {
      created.push(item);
    }
  });

  const currentIds = current.filter((i) => i.id).map((i) => i.id as string);
  const deleted = initial.filter((i) => i.id && !currentIds.includes(i.id)).map((i) => i.id!);

  return { updated, created, deleted };
};
